// MyProvider.js
import React, { ReactNode, useState, useEffect, useCallback } from "react";
import MainContext, {
  MainContextType,
  NotificationType,
  coachmonthtstats,
  courtCoachType,
  courtMonthlyStats,
  locationTypes,
  monthlyRate,
  rateDetails,
  weeklyRate,
} from "./MainContext";
import moment from "moment";
import { useSupportUnreadCount } from "src/features/SupportHub/hooks/useSupportUnreadCount";
import { watchClubChangeFromOtherTabs } from "src/utils/clubTabSync";

// Define the type for the props of MainProvider
interface MainProviderProps {
  children: ReactNode;
}

interface Role {
  name: string;
}

interface Club {
  id: number;
  club_name: string;
}

interface OtherPermissions {
  view_permissions: boolean;
  edit_permissions: boolean;
  sales: boolean;
  occupancy: boolean;
  customers: boolean;
  // StaffManager-only gate for the whole Statistics section (default true).
  stats_access?: boolean;
  // Expiring Soon page (memberships + credits expiring soon). Absent = allowed.
  expiring_view?: boolean;
  // GZ#3705 rolling calendar window, in days either side of today. Absent or
  // null = unlimited in that direction (every account until a ClubAdmin sets one).
  calendar_past_days?: number | null;
  calendar_future_days?: number | null;
  sport_permission?: number[];
}

interface LocationPermission {
  location_id: number;
}

interface User {
  id: number;
  full_name: string;
  email: string;
  address: string | null;
  role: Role;
  club: Club;
  OtherPermissions: OtherPermissions;
  // getMe returns the permissions row under the lowercase key; keep both so
  // callers can read whichever the active endpoint populated.
  otherPermissions?: OtherPermissions;
  LocationPermissions: LocationPermission[];
}

const permissionObj = {
  edit_permissions: false,
  occupancy: false,
  sales: false,
  view_permissions: false,
  customer: false,
};

const MainProvider: React.FC<MainProviderProps> = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [LoginData, setLoginData] = useState<User>();
  const [BackgroundEvent, setBackgorundEvent] = useState<any>();
  const [Notification, setNotification] = useState<NotificationType>({
    message: "",
    type: "",
    subMessage: "",
    isShow: false,
  });
  const [isCancelBooking, setIsCancelBooking] = useState<boolean>(false);
  const [UpdatePayments, setUpdatePayments] = useState<boolean>(false);
  const [selectedRegion, setSelectedRegion] = useState<any>();
  const [selectedClickEvent, setSelectedClickEvent] = useState<any>();
  const [courtMonthlyStats, setCourtMonthlystats] = useState<courtMonthlyStats>(
    { occupiedHours: 0, occupancyRate: 0, by_sport: [] }
  );
  const [selectedLocationId, setSelectedLocationId] = useState<number>(0);
  const [sport_id, setSport_id] = useState([]);
  const [sideBar, setSideBar] = useState(false);
  const [MobileSideBar, setMobileSideBar] = useState(false);
  const [ActiveTab, setActiveTab] = useState("");
  const [selectedDate, setSelectedDate] = useState(moment().toDate());
  const [selectedMonth, setSelectedMonth] = useState<any>(moment());
  const [resServicesList, setServicesList] = useState([]);
  const [CancelledPlayer, setCancelledPlayer] = useState<any[]>([]);
  const [isPermissionOn, setIsPermissionOn] = useState<boolean>(false);
  const [SelectedLocation, setSelectedLocation] = useState<locationTypes>({
    id: 0,
    location_name: "all location",
    currency: "IDR",
  });
  const [CoachMonthStats, setCoachMonthStats] = useState<coachmonthtstats>({
    occupancyRate: 0,
    overallTotalEventsHours: 0,
    eventPercent: 0,
    overallTotalLessonsHours: 0,
    lessonsPercent: 0,
  });
  const [customerList, setCustomerList] = useState([]);
  const [playerList, setPlayerList] = useState([]);
  const [selCustomer, setSelCustomer] = useState({});
  const [selPlayer, setSelPlayer] = useState({});
  const [SelectedCourtDurations, setSelectedCourtDurations] = useState<any[]>(
    []
  );

  const [selectedCustomerid, setSelectedCustomerId] = useState<any>();
  const [serviceList, setServiceList] = useState<any>([]);
  const [ratingDetails, setRatingDetails] = useState<rateDetails>({
    bookingPercent: 0,
    eventPercent: 0,
    lessonPercent: 0,
    noShowCounts: 0,
    openMatchCounts: 0,
    pendingPayments: 0,
    unfilledOpenMatchCounts: 0,
    dailyOccupancyRate: "0",
    dailyOccupancyHours: 0,
    by_sport: [],
  });
  const [monthlyGraph, setMonthlyGraph] = useState<monthlyRate>({
    bookingPercentage: 0,
    lessonPercentage: 0,
    eventPercentage: 0,
    openMatchCounts: 0,
    totalPendingPayments: 0,
  });
  const [weeklyGraph, setWeeklyGraph] = useState<weeklyRate>({
    coachesDetails: [],
    noShowCounts: 0,
    pendingPayments: 0,
  });
  const [event, setEvent] = useState({});
  const [selectedPlayers, setSelectdPlayers] = useState<any>([]);
  const [courts, setCourts] = useState<courtCoachType>({
    Pererene: ["Court 1"],
  });
  const [selectedCourt, setselectedCourt] = useState<any[]>([]);
  const [coaches, setCoaches] = useState<courtCoachType>({
    Pererene: ["tash"],
  });
  const [chatURL, setChatURL] = useState<string>("");
  const [servicePlayers, setServicePlayers] = useState<any[]>([]);
  const [onDragCourt, setOnDragCourt] = useState<any>();
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [hasUnreadSupport, setHasUnreadSupport] = useState(false);
  const [calendarSearchQuery, setCalendarSearchQuery] = useState("");

  // Global support unread listener - updates sidebar dot
  const handleHasUnreadChange = useCallback((hasUnread: boolean) => {
    setHasUnreadSupport(hasUnread);
  }, []);

  useSupportUnreadCount({ onHasUnreadChange: handleHasUnreadChange });


  // Another tab switched club / logged in / logged out: this tab's location
  // and cache belong to the old club, so reload instead of mixing them.
  useEffect(() => watchClubChangeFromOtherTabs(), []);

  useEffect(() => {
    const handleNetworkError = (e: any) => {
      setNotification({
        message: e.detail?.message || "Something went wrong",
        type: "error",
        subMessage: "",
        isShow: true,
      });
    };
    window.addEventListener("api-network-error", handleNetworkError);
    return () => window.removeEventListener("api-network-error", handleNetworkError);
  }, []);

  const contextValue: MainContextType = {
    selectedLocationId,
    setSelectedLocationId,
    setSelectedClickEvent,
    selectedClickEvent,
    setCoachMonthStats,
    CoachMonthStats,
    setSelectedLocation,
    SelectedLocation,
    UpdatePayments,
    setNotification,
    selectedCourt,
    setselectedCourt,
    Notification,
    setCourtMonthlystats,
    courtMonthlyStats,
    setUpdatePayments,
    authenticated,
    setAuthenticated,
    selectedCustomerid,
    setIsPermissionOn,
    isPermissionOn,
    chatURL,
    setChatURL,
    setSelectedCustomerId,
    resServicesList,
    setServicesList,
    customerList,
    setLoginData,
    LoginData,
    setCustomerList,
    setMobileSideBar,
    MobileSideBar,
    playerList,
    setPlayerList,
    selCustomer,
    setSelCustomer,
    setSelectedCourtDurations,
    SelectedCourtDurations,
    selPlayer,
    setSelPlayer,
    setSelectedRegion,
    selectedRegion,
    CancelledPlayer,
    setCancelledPlayer,
    BackgroundEvent,
    setBackgorundEvent,
    isCancelBooking,
    setIsCancelBooking,
    event,
    setEvent,
    servicePlayers,
    setServicePlayers,
    selectedPlayers,
    setSelectdPlayers,
    ActiveTab,
    setActiveTab,
    selectedMonth,
    setSelectedMonth,
    ratingDetails,
    setRatingDetails,
    monthlyGraph,
    setMonthlyGraph,
    weeklyGraph,
    setWeeklyGraph,
    courts,
    setCourts,
    coaches,
    setCoaches,
    serviceList,
    setServiceList,
    selectedDate,
    setSelectedDate,
    sideBar,
    setSideBar,
    sport_id,
    setSport_id,
    setOnDragCourt,
    onDragCourt,
    isChatVisible,
    setIsChatVisible,
    hasUnreadSupport,
    setHasUnreadSupport,
    calendarSearchQuery,
    setCalendarSearchQuery,
  };

  return (
    <MainContext.Provider value={contextValue}>{children}</MainContext.Provider>
  );
};

export default MainProvider;
